import { useState, useEffect, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  IconButton,
  Chip,
  CircularProgress,
  Box,
  Checkbox,
  Button,
  Fab,
  Badge,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import FilterListIcon from '@mui/icons-material/FilterList'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import api from '../services/api'

const DatabasePage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const [cameras, setCameras] = useState([])
  const [loading, setLoading] = useState(true)
  const [orderBy, setOrderBy] = useState('manufacturer')
  const [order, setOrder] = useState('asc')
  const [selectedCameras, setSelectedCameras] = useState([])

  useEffect(() => {
    const fetchCameras = async () => {
      setLoading(true)
      try {
        const data = await api.getCameras('', orderBy, order)
        setCameras(data)
      } catch (error) {
        console.error('Failed to fetch cameras:', error)
        setCameras([])
      } finally {
        setLoading(false)
      }
    }

    fetchCameras()
  }, [orderBy, order])

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleSelectCamera = (camera) => {
    setSelectedCameras(prev => {
      const isSelected = prev.find(c => c.id === camera.id)
      if (isSelected) {
        return prev.filter(c => c.id !== camera.id)
      } else {
        return [...prev, camera]
      }
    })
  }

  const handleSelectAll = () => {
    if (selectedCameras.length === filteredAndSortedCameras.length) {
      setSelectedCameras([])
    } else {
      setSelectedCameras([...filteredAndSortedCameras])
    }
  }

  const handleCompare = () => {
    if (selectedCameras.length === 0) return

    // Create comparison ID
    const comparisonId = Date.now().toString()

    // Set expiry time to 24 hours from now
    const expiryTime = new Date()
    expiryTime.setHours(expiryTime.getHours() + 24)

    // Store in localStorage
    const comparison = {
      cameras: selectedCameras,
      expiryTime: expiryTime.toISOString(),
      createdAt: new Date().toISOString(),
    }

    localStorage.setItem(`comparison_${comparisonId}`, JSON.stringify(comparison))

    // Navigate to comparison page
    navigate(`/compare?id=${comparisonId}`)
  }

  const filteredAndSortedCameras = useMemo(() => {
    let filtered = cameras.filter((camera) => {
      const query = searchQuery.toLowerCase()
      return (
        camera.manufacturer.toLowerCase().includes(query) ||
        camera.model.toLowerCase().includes(query) ||
        camera.type.toLowerCase().includes(query)
      )
    })

    // Sort
    filtered.sort((a, b) => {
      let aValue = a[orderBy]
      let bValue = b[orderBy]

      // Handle string comparison
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (aValue < bValue) return order === 'asc' ? -1 : 1
      if (aValue > bValue) return order === 'asc' ? 1 : -1
      return 0
    })

    return filtered
  }, [cameras, searchQuery, order, orderBy])

  const columns = [
    { id: 'manufacturer', label: 'Manufacturer', sortable: true },
    { id: 'model', label: 'Camera Model', sortable: true },
    { id: 'megapixels', label: 'Megapixels', sortable: true },
    { id: 'sensors', label: 'Sensor Count', sortable: true },
    { id: 'type', label: 'Type', sortable: true },
    { id: 'productLink', label: 'Product Link', sortable: false },
  ]

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto px-4 py-8 max-w-7xl"
    >
      {/* Header */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="mb-8"
      >
        <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-2">
          Camera Database
        </h1>
        <p className="text-gray-400">
          Browse and filter through our comprehensive CCTV camera database
        </p>
      </motion.div>

      {/* Search field */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="mb-6"
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by manufacturer, model, or type..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(26, 26, 26, 0.8)',
              backdropFilter: 'blur(10px)',
              borderRadius: '0.75rem',
              '& fieldset': {
                borderColor: 'rgba(59, 130, 246, 0.3)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(59, 130, 246, 0.6)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#3b82f6',
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#3b82f6' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <FilterListIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
              </InputAdornment>
            ),
          }}
        />
      </motion.div>

      {/* Results count and Compare Button */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="mb-4 flex items-center justify-between flex-wrap gap-4"
      >
        <Chip
          label={`${filteredAndSortedCameras.length} camera${filteredAndSortedCameras.length !== 1 ? 's' : ''} found`}
          sx={{
            backgroundColor: 'rgba(59, 130, 246, 0.2)',
            color: '#3b82f6',
            fontWeight: 'bold',
          }}
        />

        {selectedCameras.length > 0 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 500 }}
          >
            <Button
              variant="contained"
              startIcon={<CompareArrowsIcon />}
              onClick={handleCompare}
              sx={{
                backgroundColor: '#8b5cf6',
                '&:hover': {
                  backgroundColor: '#7c3aed',
                },
              }}
            >
              Compare {selectedCameras.length} Camera{selectedCameras.length !== 1 ? 's' : ''}
            </Button>
          </motion.div>
        )}
      </motion.div>

      {/* Table */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: 'rgba(26, 26, 26, 0.8)',
            backdropFilter: 'blur(10px)',
            borderRadius: '1rem',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            maxHeight: 'calc(100vh - 350px)',
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    sx={{
                      backgroundColor: 'rgba(10, 10, 10, 0.95)',
                      borderBottom: '2px solid rgba(59, 130, 246, 0.3)',
                    }}
                  >
                    <Checkbox
                      checked={selectedCameras.length === filteredAndSortedCameras.length && filteredAndSortedCameras.length > 0}
                      indeterminate={selectedCameras.length > 0 && selectedCameras.length < filteredAndSortedCameras.length}
                      onChange={handleSelectAll}
                      sx={{
                        color: 'rgba(59, 130, 246, 0.5)',
                        '&.Mui-checked': {
                          color: '#3b82f6',
                        },
                        '&.MuiCheckbox-indeterminate': {
                          color: '#3b82f6',
                        },
                      }}
                    />
                  </TableCell>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      sx={{
                        backgroundColor: 'rgba(10, 10, 10, 0.95)',
                        borderBottom: '2px solid rgba(59, 130, 246, 0.3)',
                        fontWeight: 'bold',
                        fontSize: '0.95rem',
                        color: '#3b82f6',
                      }}
                    >
                      {column.sortable ? (
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : 'asc'}
                          onClick={() => handleSort(column.id)}
                          sx={{
                            '&.MuiTableSortLabel-root': {
                              color: '#3b82f6',
                            },
                            '&.MuiTableSortLabel-root:hover': {
                              color: '#60a5fa',
                            },
                            '&.Mui-active': {
                              color: '#60a5fa',
                            },
                            '& .MuiTableSortLabel-icon': {
                              color: '#60a5fa !important',
                            },
                          }}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedCameras.map((camera, index) => {
                  const isSelected = selectedCameras.find(c => c.id === camera.id)
                  return (
                    <motion.tr
                      key={camera.id}
                      component={TableRow}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(59, 130, 246, 0.05)',
                        },
                        backgroundColor: isSelected ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={!!isSelected}
                          onChange={() => handleSelectCamera(camera)}
                          sx={{
                            color: 'rgba(59, 130, 246, 0.5)',
                            '&.Mui-checked': {
                              color: '#8b5cf6',
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>{camera.manufacturer}</TableCell>
                    <TableCell sx={{ fontWeight: 'medium' }}>
                      {camera.model}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${camera.megapixels} MP`}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(139, 92, 246, 0.2)',
                          color: '#a78bfa',
                        }}
                      />
                    </TableCell>
                    <TableCell>{camera.sensors}</TableCell>
                    <TableCell>
                      <Chip
                        label={camera.type}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: 'rgba(59, 130, 246, 0.5)',
                          color: '#3b82f6',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        href={camera.product_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: '#3b82f6',
                          '&:hover': {
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                            transform: 'scale(1.1)',
                          },
                          transition: 'all 0.2s',
                        }}
                      >
                        <OpenInNewIcon />
                      </IconButton>
                    </TableCell>
                  </motion.tr>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </motion.div>

      {/* No results message */}
      {!loading && filteredAndSortedCameras.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center mt-12"
        >
          <SearchIcon sx={{ fontSize: 80, color: 'rgba(255, 255, 255, 0.1)' }} />
          <p className="text-gray-500 text-lg mt-4">
            No cameras found matching your search
          </p>
        </motion.div>
      )}
    </motion.div>
  )
}

export default DatabasePage
